// FLEXX timezone utilities — UTC-canonical storage + user-local rendering.
//
// All recurring "live" schedule slots are authored by the admin in a single
// canonical source timezone (PHT — Asia/Manila, no DST) and rendered in
// every viewer's local timezone via these helpers.
//
// Single-source-of-truth for FLEXX clock conventions. Nothing else in the
// codebase should call `Intl.DateTimeFormat` for live scheduling.

export const LIVE_SOURCE_TZ = "Asia/Manila"; // PHT — admin authoring TZ
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** Browser's resolved IANA timezone name (e.g. "Asia/Dubai"). */
export function getUserTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch (_) { return "UTC"; }
}

/** Short TZ abbreviation for a given moment ("GMT+4", "PHT", "EST"). */
export function getUserTzAbbr(date = new Date(), tz = getUserTz()) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" }).formatToParts(date);
    return parts.find((p) => p.type === "timeZoneName")?.value || tz;
  } catch (_) { return tz; }
}

/** Read a Date's wall-clock parts (year/month/day/hour/min/dow) in a target TZ. */
function partsInTz(date, tz) {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", weekday: "short", hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(date).map((p) => [p.type, p.value]));
  return {
    year: +parts.year, month: +parts.month, day: +parts.day,
    hour: parts.hour === "24" ? 0 : +parts.hour, minute: +parts.minute,
    weekday: parts.weekday,
  };
}

/** UTC offset (in minutes, +ve east of UTC) of a TZ at a given moment. */
function tzOffsetMin(date, tz) {
  // Strip sub-minute precision so Math.round can't flip when the probe
  // has 30+ seconds — keeps the offset stable for whole-minute slots.
  const clean = new Date(Math.floor(date.getTime() / 60000) * 60000);
  const p = partsInTz(clean, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  return Math.round((asUtc - clean.getTime()) / 60000);
}

/**
 * Resolve the next occurrence of a recurring weekly slot authored in `sourceTz`.
 * @param {string} dayName  full day name e.g. "Monday"
 * @param {string} hhmm     "HH:mm" in 24h sourceTz wall-clock
 * @param {string} sourceTz IANA tz (defaults to PHT)
 * @returns {Date}          the next occurrence as a real UTC Date, or null
 */
export function nextOccurrence(dayName, hhmm, sourceTz = LIVE_SOURCE_TZ) {
  if (!dayName || !hhmm) return null;
  const targetDow = DAY_NAMES.indexOf(dayName);
  if (targetDow < 0) return null;
  const [hh, mm] = hhmm.split(":").map(Number);
  const now = new Date();

  // Walk forward up to 8 days looking for (sourceTz weekday === targetDow) at hhmm in source.
  for (let offset = 0; offset < 8; offset++) {
    const probe = new Date(now.getTime() + offset * 86400000);
    const p = partsInTz(probe, sourceTz);
    const probeDow = DAY_NAMES.findIndex((d) => d.startsWith(p.weekday));
    if (probeDow !== targetDow) continue;
    // Construct the target moment by interpreting (year/month/day, hh, mm) AS sourceTz wall-clock,
    // then converting to a real UTC instant using sourceTz's current offset.
    const offsetMin = tzOffsetMin(probe, sourceTz);
    const targetUtcMs = Date.UTC(p.year, p.month - 1, p.day, hh, mm) - offsetMin * 60000;
    if (targetUtcMs > now.getTime()) return new Date(targetUtcMs);
  }
  return null;
}

/** Format a Date in the user's local timezone as "h:mm AM/PM". */
export function formatLocalClock(date, tz = getUserTz()) {
  if (!date) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true,
  }).format(date);
}

/** Format as "Tue · 5:00 PM" for a Date in user's local TZ. */
export function formatLocalDayClock(date, tz = getUserTz()) {
  if (!date) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz, weekday: "short", hour: "numeric", minute: "2-digit", hour12: true,
  }).format(date);
}

/** True if the given Date falls on the same calendar day as `now`, in `tz`. */
export function isSameLocalDay(date, tz = getUserTz(), now = new Date()) {
  if (!date) return false;
  const a = partsInTz(date, tz);
  const b = partsInTz(now, tz);
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

/** User's local day name today (e.g. "Wednesday"). */
export function todayLocalName(tz = getUserTz()) {
  const p = partsInTz(new Date(), tz);
  // partsInTz returns short weekday; expand to full name.
  const idx = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(p.weekday);
  return idx >= 0 ? DAY_NAMES[idx] : "";
}

/**
 * Returns { localTime, localDay, sameDay, sourceLabel } for a recurring weekly slot.
 * Used by the LiveRoom schedule grid to render "5:00 PM (Wed)" or just "5:00 PM" if
 * the session lands on the viewer's same calendar day.
 */
export function describeSlotForUser(dayName, hhmm, sourceTz = LIVE_SOURCE_TZ) {
  const occ = nextOccurrence(dayName, hhmm, sourceTz);
  if (!occ) return { localTime: "—", localDay: "", sameDay: false, sourceLabel: hhmm };
  const userTz = getUserTz();
  const localTime = formatLocalClock(occ, userTz);
  const localDay = new Intl.DateTimeFormat("en-US", { timeZone: userTz, weekday: "short" }).format(occ);
  const sameDay = isSameLocalDay(occ, userTz);
  const sourceLabel = `${formatHhmm(hhmm)} ${getUserTzAbbr(occ, sourceTz)}`;
  return { localTime, localDay, sameDay, sourceLabel, occursAt: occ };
}

/** "21:00" → "9:00 PM". */
export function formatHhmm(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const ap = h >= 12 ? "PM" : "AM";
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}

/** Active trading session for a given moment ("Asian"/"London"/"NY"/"Overlap"/"Off"). */
export function activeSession(date = new Date()) {
  // Sessions defined in UTC: Asian 23:00-07:00, London 07:00-16:00, NY 12:00-21:00.
  // Overlap (London+NY) 12:00-16:00 UTC. Outside London/NY/Asian = "Off".
  const u = date.getUTCHours() + date.getUTCMinutes() / 60;
  const inAsian = u >= 23 || u < 7;
  const inLondon = u >= 7 && u < 16;
  const inNY = u >= 12 && u < 21;
  if (inLondon && inNY) return "Overlap (London+NY)";
  if (inLondon) return "London";
  if (inNY) return "New York";
  if (inAsian) return "Asian";
  return "Off-session";
}

// ── TRADING SESSION CLOCK ───────────────────────────────────────────
// Canonical FLEXX session windows in UTC. A dual-active window (e.g.
// Tokyo+London 07:00-08:00 or London+NY 12:00-16:00) is rendered as an
// "Overlap" pseudo-session so the trader sees the highest-volume context.
export const TRADING_SESSIONS = [
  { id: "tokyo",   name: "Tokyo",     short: "TKO", color: "#B57BFF", startUtc: 0,  endUtc: 9,  desc: "Asian session — low vol, range-bound. Trade smaller, wait for London." },
  { id: "london",  name: "London",    short: "LDN", color: "#4A90E2", startUtc: 7,  endUtc: 16, desc: "Highest volume window. A+ setups deserve full risk." },
  { id: "newyork", name: "New York",  short: "NY",  color: "#FF6B35", startUtc: 12, endUtc: 21, desc: "Continuation + reversal session. Watch for fade plays after London close." },
];

// Compute the timeline of the next 24h of sessions for the viewer.
// Returns an array of session windows in chronological order with active flag,
// time-to-start / time-to-end (ms), and progress (0..1) for active windows.
export function getSessionTimeline(now = new Date(), tz = getUserTz()) {
  const out = [];
  const nowMs = now.getTime();
  // Walk -1 → +2 days so we always have a previous + next session in window.
  for (let dayOffset = -1; dayOffset <= 2; dayOffset++) {
    const probe = new Date(nowMs + dayOffset * 86400000);
    const probeUtcDay = Date.UTC(probe.getUTCFullYear(), probe.getUTCMonth(), probe.getUTCDate());
    for (const s of TRADING_SESSIONS) {
      const startMs = probeUtcDay + s.startUtc * 3600000;
      const endMs = probeUtcDay + s.endUtc * 3600000;
      out.push({
        ...s,
        startAt: new Date(startMs),
        endAt: new Date(endMs),
        startMs, endMs,
        isActive: nowMs >= startMs && nowMs < endMs,
        msToStart: startMs - nowMs,
        msToEnd: endMs - nowMs,
        progress: nowMs >= startMs && nowMs < endMs ? (nowMs - startMs) / (endMs - startMs) : null,
        startLocal: formatLocalClock(new Date(startMs), tz),
        endLocal: formatLocalClock(new Date(endMs), tz),
      });
    }
  }
  return out.sort((a, b) => a.startMs - b.startMs);
}

// Resolve the "current state" — primary active session(s), the next session
// to begin, and the previous session that just ended. Used by SessionsClock.
export function getSessionState(now = new Date(), tz = getUserTz()) {
  const timeline = getSessionTimeline(now, tz);
  const active = timeline.filter((s) => s.isActive);
  // Overlap label: when both London and NY are active simultaneously.
  let primary = null;
  if (active.length >= 2) {
    const names = active.map((s) => s.id).sort().join("+");
    if (names.includes("london") && names.includes("newyork")) {
      primary = {
        id: "overlap-ldn-ny", name: "London+NY Overlap", short: "OVERLAP",
        color: "#FFD700",
        desc: "Highest-volume window of the day. Best A-grade execution opportunities.",
        endAt: new Date(Math.min(...active.map((s) => s.endMs))),
        startAt: new Date(Math.max(...active.map((s) => s.startMs))),
        progress: null, // mixed
      };
      primary.msToEnd = primary.endAt.getTime() - now.getTime();
      primary.startLocal = formatLocalClock(primary.startAt, tz);
      primary.endLocal = formatLocalClock(primary.endAt, tz);
    } else {
      primary = active[0]; // fallback
    }
  } else if (active.length === 1) {
    primary = active[0];
  }
  // Next + previous in chronological order
  const upcoming = timeline.filter((s) => s.startMs > now.getTime()).slice(0, 1)[0] || null;
  const past = [...timeline].reverse().find((s) => s.endMs <= now.getTime()) || null;
  return { primary, active, upcoming, previous: past, timeline };
}

// "00d 02h 14m 33s" (or shorter variants depending on size)
export function formatDuration(ms) {
  if (!ms || ms < 0) return "00:00:00";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
